import Vue from "vue";
import { ModelType } from "~/enums/modelType";
import { VersionType } from "~/enums/versionType";
import { BarLayoutOptions, DataPointType, VisualizationType } from "~/enums/visualizationType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { Hash } from "~/interfaces/hashInterfaces";
import Validatable from "~/mixins/validatable";
import ErrorHandler from "~/models/errorHandling/errorHandler";
import ObjectModel from "~/models/objectModel";
import Organization from "~/models/organization";
import { RelationType } from "~/models/relationType";
import Share from "~/models/share";
import VisualizationBuilderModel from "~/models/tracked/visualizationBuilderModel";
import * as dataTableUtils from "~/models/visualizations/dataTableUtils";
import VisualizationDataFusionPoint from "~/models/visualizations/visualizationDataFusionPoint";
import VisualizationDataPoint from "~/models/visualizations/visualizationDataPoint";
import VisualizationDataTableFilter from "~/models/visualizations/visualizationDataTableFilter";
import VisualizationSource from "~/models/visualizations/visualizationSource";
import VisualizationSourceForm from "~/models/visualizations/visualizationSourceForm";
import VisualizationSourceObjectModel from "~/models/visualizations/visualizationSourceObjectModel";
import VisualizationProvider from "~/providers/visualizationProvider";
import ElementHandler from "~/services/elementHandler";
import { IPeekerOptions } from "~/services/peeker";
import store from "~/state/store";
import Capable from "../../../common/mixins/capable";
import Shareable from "../../../common/mixins/shareable";
import Form from "../../../formability/form_builder/models/form";
import { DataPointModel } from "../../../formability/visualizationBuilder/enums/builder-area";
import { StaticDataPointLabels } from "../../../formability/visualizationBuilder/enums/data-point-static-type";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import VisualizationBuilderValidation from "../navigableValidations/visualizationBuilderValidation";
import VisualizationSourceDataPoint from "./visualizationSourceDataPoint";
import {DocumentType} from "~/enums/documentType";

export default class Visualization extends Shareable(Validatable(Capable(VisualizationBuilderModel))) {
  rootModel: Visualization;
  displayName: string;
  _organization: Organization;
  _classKey = "visualization";
  title: string;
  author: {first_name: string, last_name: string};
  is_published: boolean;
  is_archived: boolean;
  visualization_data_points: { [key: string]: VisualizationSourceDataPoint } = {};
  reference_name: string;
  sources: Hash<VisualizationSource> = {};
  visualization_type;
  value_data_points: Hash<VisualizationDataPoint> = {};
  row_data_points: Hash<VisualizationDataPoint> = {};
  column_data_points: Hash<VisualizationDataPoint> = {};
  data_fusion_points: Hash<VisualizationDataFusionPoint> = {};
  data_table_filters: Hash<VisualizationDataTableFilter> = {};
  draggedDataPointType = "";
  published_visualization_id: string;
  seenDefineTooltip: boolean;
  seenLivePreviewTooltip: boolean;
  seenWorkflowStepFilterHelp: boolean;
  attempted_to_publish: boolean;
  bar_layout: BarLayoutOptions;
  liveData: Hash;
  visualization_id: string;

  get isPublishedVisualization(){
    return !!this.visualization_id
  }

  except = ['visualization', 'sources', 'createEmbeddedObject']

  static repo: IRepo<Visualization, VisualizationProvider> = new Repo<Visualization, VisualizationProvider>('visualizations', Visualization, {}, new VisualizationProvider(Visualization, 'visualization', 'visualizations'));
  repo: IRepo<this, VisualizationProvider<this>>;


  get valueDataPointsArray(): VisualizationDataPoint[] {
    return ObjectHelper.toArray(this.value_data_points)
  }
  _valueDataPointsHandler: ElementHandler = new ElementHandler(this, Visualization, 'value_data_points');
  get valueDataPointsHandler(): ElementHandler {
    return this._valueDataPointsHandler;
  }

  get rowDataPointsArray(): VisualizationDataPoint[] {
    return ObjectHelper.toArray(this.row_data_points)
  }
  _rowDataPointsHandler: ElementHandler = new ElementHandler(this, Visualization, 'row_data_points');
  get rowDataPointsHandler(): ElementHandler {
    return this._rowDataPointsHandler;
  }

  get columnDataPointsArray(): VisualizationDataPoint[] {
    return ObjectHelper.toArray(this.column_data_points)
  }
  _columnDataPointsHandler: ElementHandler = new ElementHandler(this, Visualization, 'column_data_points');
  get columnDataPointsHandler(): ElementHandler {
    return this._columnDataPointsHandler;
  }

  get dataFusionPointsArray(): VisualizationDataFusionPoint[] {
    return ObjectHelper.toArray(this.data_fusion_points)
  }
  get dataSourcesArray(): VisualizationSource[] {
    return ObjectHelper.toArray(this.sources)
  }
  _dataFusionPointsHandler: ElementHandler = new ElementHandler(this, Visualization, 'data_fusion_points');
  get dataFusionPointsHandler(): ElementHandler {
    return this._dataFusionPointsHandler;
  }

  get dataTableFiltersArray(): VisualizationDataTableFilter[] {
    return ObjectHelper.toArray(this.data_table_filters).sort((a, b) => a.sort_order - b.sort_order);
  }
  _dataTableFiltersHandler = new ElementHandler(this, Visualization, 'data_table_filters');
  get dataTableFiltersHandler(): ElementHandler {
    return this._dataTableFiltersHandler;
  }

  get dataCollected(): boolean {
    return this.liveData ? true : false;
  }

  get hasData(): boolean {
    if (!this.dataCollected) {
      return false;
    }

    if (this.visualization_type === VisualizationType.PivotTable) {
      return this.allDataPoints.some(point => point.tempHeaders.length > 0);
    }

    if (this.liveData.row_agg_c) {
      return this.liveData.row_agg_c.buckets.length > 0;
    } else if (this.liveData.col_agg_c) {
      return this.liveData.col_agg_c.buckets.length > 0;
    } else if (this.liveData.length) {
      return this.liveData.length > 0;
    } else {
      return this.liveData.val_agg_c ? this.liveData.val_agg_c.value || this.liveData.val_agg_c.value === 0 : false;
    }
  }

  get collectionKey() {
    return 'visualizations';
  }

  get classKey(): string {
    return "visualization";
  }

  get docType(): DocumentType {
    return DocumentType.visualization;
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'sources': { type: VisualizationSource, relationType: RelationType.dictionary },
      'value_data_points': { type: VisualizationDataPoint, relationType: RelationType.dictionary },
      'row_data_points': { type: VisualizationDataPoint, relationType: RelationType.dictionary },
      'column_data_points': { type: VisualizationDataPoint, relationType: RelationType.dictionary },
      'data_fusion_points': { type: VisualizationDataFusionPoint, relationType: RelationType.dictionary },
      'data_table_filters': { type: VisualizationDataTableFilter, relationType: RelationType.dictionary },
      'shares': { type: Share, relationType: RelationType.collection}
    };
  }

  addToRepo(): void {
    Visualization.repo.add(this);
  }

  createEmbeddedObject = {
    sources: (parent, json) => {
      let type = json?.friendly_type;
      let obj: VisualizationSource;

      switch (type) {
        case ModelType.Form:
          obj = new VisualizationSourceForm();
          break;
        case ModelType.ObjectModel:
          obj = new VisualizationSourceObjectModel();
          break;
        default:
          break;
      }

      if (obj) obj._nameInParent = 'sources';
      if (obj) obj.parent = this;
      if (obj instanceof VisualizationSourceForm) obj.addToRepo()

      return obj;
    },
    value_data_points: (parent, json) => {
      return this.createDataPoint(json, 'value_data_points');
    },
    row_data_points: (parent, json) => {
      return this.createDataPoint(json, 'row_data_points');
    },
    column_data_points: (parent, json) => {
      return this.createDataPoint(json, 'column_data_points');
    },
    data_fusion_points: (parent, json) => {
      const obj = new VisualizationDataFusionPoint(json, this)
      obj.parent = this
      obj._nameInParent = 'data_fusion_points'
      obj.addToRepo()
      return obj
    },
    data_table_filters: (parent, json) => {
      return VisualizationDataTableFilter.repo.findOrCreate(json.id, json.client_id);
    },
  };

  createDataPoint(json, nameInParent) {
    let obj: VisualizationDataPoint;

    switch (json.friendly_type) {
      case DataPointModel.dataFusion:
        obj = new VisualizationDataFusionPoint(json, this);
        break;
      case DataPointModel.dataPoint:
        obj = new VisualizationSourceDataPoint(json, this)
        break;
    }

    obj.parent = this;
    obj._nameInParent = nameInParent;
    obj.addToRepo();

    return obj;
  }

  get publishedStatus(): string {
    return this.is_published ? 'Published' : 'Unpublished';
  }

  get hasSeenDefineYourVariableHelp(): boolean {
    this.seenDefineTooltip = localStorage.getItem("formability_define_variable_help_balloon") !== null;
    return this.seenDefineTooltip;
  }

  async generateData(filter: object, isPreview = false) {
    try {
      const res = await $.ajax({
        url: `/visualizations/${this.id}/live_data`,
        type: 'GET',
        dataType: 'json',
        data: {
          filter,
          mode: isPreview ? "preview" : "live"
        },
      })

      if (this.visualization_type === VisualizationType.PivotTable) {
        dataTableUtils.clearData(this);
        dataTableUtils.formatTable(this, res.data, 1, 1);
      }

      Vue.set(this, "liveData", res.data);
      return res.data;
    } catch (e) {
      console.error(e)
      return [];
    }
  }

  async loadData(data) {
    try {
      if (this.visualization_type === VisualizationType.PivotTable) {
        dataTableUtils.clearData(this);
        dataTableUtils.formatTable(this, data, 1, 1);
      }

      Vue.set(this, "liveData", data);
      return data;
    } catch (e) {
      console.error(e)
      return [];
    }
  }

  get unpublishedSources() {
    return this.allDataPoints.some(p => !p.isPublished) ||
      this.dataTableFiltersArray.some(f => f.unpublishedSources);
  }

  setHasSeenDefineYourVariableHelp() {
    this.seenDefineTooltip = true;
    store.commit(`${this.builderStoreName}/setSeenDefineTooltip`);
    localStorage.setItem("formability_define_variable_help_balloon", "true");
  }

  get hasSeenPreviewLiveDataHere(): boolean {
    this.seenLivePreviewTooltip = localStorage.getItem("formability_preview_live_data_help_balloon") !== null;
    return this.seenLivePreviewTooltip;
  }

  setHasSeenPreviewLiveDataHere() {
    this.seenLivePreviewTooltip = true;
    localStorage.setItem("formability_preview_live_data_help_balloon", "true");
  }

  get hasSeenWorkflowStepFilterHelp() : boolean {
    this.seenWorkflowStepFilterHelp = localStorage.getItem("formability_workflow_step_filter_help_balloon") !== null;
    return this.seenWorkflowStepFilterHelp;
  }

  setHasSeenWorkflowStepFilterHelp() {
    this.seenWorkflowStepFilterHelp = true;
    localStorage.setItem("formability_workflow_step_filter_help_balloon", "true");
  }

  createSource(documentJson) {
    let obj;

    if (documentJson.type === "Visualizations::Sources::Form") {
      obj = new VisualizationSourceForm();
      (Form.repo.find(documentJson.client_id) || new Form()).loadFromJson(documentJson);
    } else {
      obj = new VisualizationSourceObjectModel();
      (ObjectModel.repo.find(documentJson.client_id) || new ObjectModel()).loadFromJson(documentJson);
    }

    if (obj) {
      obj._nameInParent = 'sources';
      obj.rootModel = this;
      obj.parent = this;
      obj.source_client_id = documentJson.client_id

      this.trackCreationDeletion(obj.client_id, null, obj.asJson(), obj);
      this.sources[obj.client_id] = obj;
      this.saveChangesToStore();
    }

    return obj
  }

  staticLabels(keys) {
    return keys.map(k => StaticDataPointLabels[k]);
  }

  createDataFusion(json) {
    let obj = new VisualizationDataFusionPoint(json, this);
    obj.rootModel = this;
    obj.parent = this;
    obj.data_point_references = json.dataPointReferences.map(ref => {
      ref.rootModel = this;
      ref.parent = obj;
      return ref;
    });

    const element = this.dataFusionPointsHandler.append(DataPointType.Fusion, this.dataFusionPointsArray.length, obj);
    this.trackCreationDeletion(element.client_id, null, element.asJson(), element);
  }
  
  static get provider(): VisualizationProvider {
    return this.repo.provider;
  }

  tableHeader() {
    if (this.columnDataPointsArray.length > 0) {
      return this.columnDataPointsArray.reduce((arr, d) => {
        let dataSpan = d.getDataHeaders();
        let c_span = 0;

        if (d.enable_total) {
          c_span += 1;
        }

        const header = [{
          r_span: 1,
          c_span: dataSpan.length + c_span,
          title: d.title,
          isHeader: true,
          client_id: d.client_id,
        }]

        return arr.concat(header);
      }, []);
    }

    return [''];
  }

  get summaryCardHeader(): string {
    if (this.reference_name) {
      return this.reference_name;
    }

    if (this.valueDataPointsArray.length > 0) {
      return this.valueDataPointsArray[0].title;
    }

    return 'Title';
  }

  get rowTotalValue(): boolean {
    if (this.rowDataPointsArray.length === 0) {
      return false;
    }

    return this.rowDataPointsArray[0].enable_total;
  }

  get colTotalValue(): boolean {
    if (this.columnDataPointsArray.length === 0) {
      return false;
    }

    return this.columnDataPointsArray[0].enable_total;
  }

  get provider(): this['repo']['provider'] {
    return this.repo.provider;
  }

  static get displayName(): string {
    return 'Visualization';
  }

  get rootName() {
    return 'visualization';
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled Visualization";
    }
  }

  get builderStoreName(): string {
    return this.storeName;
  }

  get visualizationSources(): VisualizationSource[] {
    return ObjectHelper.toArray(this.sources);
  }

  get valueDataPoints(): VisualizationDataPoint[] {
    return ObjectHelper.toArray(this.value_data_points);
  }

  get allDataPoints() {
    return [...this.valueDataPointsArray, ...this.columnDataPointsArray, ...this.rowDataPointsArray]
  }

  get allSourceDataPoints() {
    let dataPoints = []

    this.visualizationSources.forEach(source => {
      dataPoints.push(...source.dimensions);
      dataPoints.push(...source.measures);
      dataPoints.push(source.customRowCol);
    });

    dataPoints = dataPoints.concat(this.dataFusionPointsArray);
    return dataPoints
  }

  isValue(dataPoint): boolean {
    return !!this.value_data_points[dataPoint.client_id]
  }

  isColumn(dataPoint): boolean {
    return !!this.column_data_points[dataPoint.client_id]
  }

  isRow(dataPoint): boolean {
    return !!this.row_data_points[dataPoint.client_id]
  }

  swapRowsAndColumns() {
    const tempRows = Object.assign({}, this.row_data_points);
    const tempCols = Object.assign({}, this.column_data_points);

    this.rowDataPointsArray.forEach(point => point.destroy())
    this.columnDataPointsArray.forEach(point => point.destroy())

    this.row_data_points = tempCols;
    this.column_data_points = tempRows;

    Object.values(this.rowDataPointsArray).forEach(el => {
      el._nameInParent = "row_data_points";
      this.trackCreationDeletion(el.client_id, null, el.asJson(), el);
      el.saveChangesToStore();
      el.addToRepo();
    });

    Object.values(this.columnDataPointsArray).forEach(el => {
      el._nameInParent = "column_data_points";
      this.trackCreationDeletion(el.client_id, null, el.asJson(), el);
      el.saveChangesToStore();
      el.addToRepo();
    });
  }

  get versionType(): VersionType {
    return VersionType.visualization;
  }

  get sourcesWChangedIndexedFields(): VisualizationSource[] {
    return this.visualizationSources.filter(source => {
      if (source instanceof VisualizationSourceForm) {
        return source?.changed_indexed_fields?.added_fields?.length > 0 || source?.changed_indexed_fields?.removed_fields?.length > 0
      } else {
        return false
      }
    })
  }

  clearChangedFields(): void {
    this.sourcesWChangedIndexedFields.forEach((source: VisualizationSourceForm) => {
      source.clearChangedFields()
    })
  }

  // ******************** VALIDATABLE******************** //
  _validation: VisualizationBuilderValidation = new VisualizationBuilderValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'default_sort_options': // intentional fallthrough
      case 'short_description':
        return '';
      default:
        return propName;
    }
  }

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(this.lastPeekerOptions || { area: this.currentBuilderArea(), modelId: this.clientIdOrId });
      // if (this.attempted_to_publish) {
      //   _.debounce(this.validate, 250);
      // }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.builderStoreName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      ErrorHandler.autosaveCatch(this, err);
    });
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions = { area: this.currentBuilderArea(), modelId: this.clientIdOrId }): void {
    store.commit(`${this.builderStoreName}/saveChanges`, { changes, peekerOptions });
  }

  // ******************** TRACK CHANGES END ******************** //

  initializeEmbedded(parent: this, item: any, position: number, json: object = {}, selectItem: boolean = true) {
    if (item instanceof VisualizationDataTableFilter) {
      item.dataTableFiltersHandler.updateElementPositions(parent, item, position);

      parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
      item.addToRepo();
      item.select();

      Vue.set(parent[item.rootName], item.client_id, item);
      return item;
    }
  }

}
